import * as React from "react";
import dayjs from "dayjs";
import { Calendar, User } from "lucide-react";
import { Card, CardContent } from "@app/components/ui/card";
import type { HeaderLayout, PatientSchema } from "@app/types/few-schot-schema";
import type { MageXPatient } from "@prisma/client";
import type { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import { resolveAggregatedValue } from "@app/utils/resolve-field-value";
import { asTerms } from "@app/utils/problem-list";
import { valueLabel, type ValueLabels } from "@app/utils/value-labels";

interface PatientHeaderCardProps {
  patient: MageXPatientWithFeedbacks;
  layout: HeaderLayout;
  schema: PatientSchema;
  /** Replaces the ontology badge row.
   *
   *  The badge is declared by ``header_role`` and, for rachis, that is the INDICATION. An indication
   *  is a property of an EPISODE, so hoisting it to the header turns it into the union of every
   *  episode's reason: a patient revised three years later reads as carrying two contradictory
   *  principal diagnoses at once. The page that knows about episodes passes its own summary here;
   *  every other page keeps the badges untouched. */
  badgesSlot?: React.ReactNode;
  /** Appended to the identity line: what only the page holding the pathway knows about the patient,
   *  the latest news. */
  detailsSlot?: React.ReactNode;
}

// Universal PII — every patient page reads these exact keys from the patient
// object, which is populated by ``_inject_identity`` on the backend from the
// ``PatientIdentity`` table. They never appear in the speciality ontology.
const NAME_KEYS = ["prenom", "nom"] as const;
const NISS_KEY = "num_secu";
// French 15-digit NIR-INS mask. If another country's identifier format is
// needed later, this is the one place to branch.
const NISS_MASK = "# ## ## ## ### ### ##";

const NISS_EMPTY = "N/A";
const DEFAULT_BADGE_EMPTY = "Non-posé";

// Single-char sexe codes (INS/NIR standard: M / F) → human labels that match
// the ontology enum ("Homme" / "Femme" / "N/A"). Unknown values pass through.
const SEXE_LABEL: Record<string, string> = {
  F: "Femme",
  M: "Homme",
  H: "Homme",
};

function normalizeSexe(raw: unknown): string {
  if (typeof raw !== "string" || raw.length === 0) return "";
  const key = raw.trim().toUpperCase();
  return SEXE_LABEL[key] ?? raw;
}

function applyMask(raw: string | null | undefined, mask: string): string {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  const slotCount = (mask.match(/#/g) || []).length;
  if (slotCount === 0) return String(raw);
  if (digits.length !== slotCount) {
    return mask.replace(/#/g, "X");
  }
  let i = 0;
  return mask.replace(/#/g, () => digits[i++]);
}

function readValue(patient: MageXPatient, key: string): unknown {
  return (patient as unknown as Record<string, unknown>)[key];
}

function formatBadgeValue(value: unknown, labels: ValueLabels): string {
  // A synthesised problem list (e.g. `diagnostic_principaux` on the temporal
  // projection) holds `Problem` objects, alone or beside corrected labels — join
  // only their titles so the badge reads as clean labels instead of "[object Object]".
  const terms = asTerms(value);
  if (Array.isArray(terms))
    return terms
      .filter(Boolean)
      .map((item) => valueLabel(labels, String(item)))
      .join(", ");
  if (value === null || value === undefined || value === "") return "";
  return valueLabel(labels, String(value));
}

function formatBirthday(raw: unknown): string {
  if (typeof raw !== "string" || raw.length === 0) return "";
  // Some source connectors store "0000-00-00" / "0000" as a placeholder for
  // unknown birthdays. dayjs parses these to nonsense ("30 nov. 1899" on some
  // engines); treat them as absent instead.
  if (/^0{4}(-0{2}-0{2})?$/.test(raw.trim())) return "";
  const d = dayjs(raw);
  return d.isValid() ? d.format("DD MMM YYYY") : "";
}

export function PatientHeaderCard({
  patient,
  layout,
  schema,
  badgesSlot,
  detailsSlot,
}: PatientHeaderCardProps) {
  const fullName =
    NAME_KEYS.map((k) => readValue(patient, k))
      .filter((v): v is string => typeof v === "string" && v.length > 0)
      .join(" ")
      .trim() || "Patient";

  const niss = applyMask(
    readValue(patient, NISS_KEY) as string | null | undefined,
    NISS_MASK,
  );
  const nissDisplay = niss || NISS_EMPTY;

  const birthday = formatBirthday(readValue(patient, "date_naissance"));
  const sexe = normalizeSexe(readValue(patient, "sexe"));

  return (
    <Card className="border-l-4 border-l-primary shadow-lg bg-gradient-to-br from-white to-slate-50/50">
      <CardContent className="p-6">
        <div className="flex items-center mb-3">
          <h2 className="text-2xl font-bold text-slate-900">
            {fullName}
          </h2>
          <span className="flex-1 relative mx-4 before:h-px before:w-full before:bg-slate-200 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2" />
          <span
            className="font-mono text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-lg"
          >
            {nissDisplay}
          </span>
        </div>
        {badgesSlot !== undefined && <div className="mb-1">{badgesSlot}</div>}
        {badgesSlot === undefined && layout.badges.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-base">
            {layout.badges.map((badge) => {
              // The projected value, as the ontology field rows read it: a doctor's correction on
              // the mirrored field is already in it.
              const resolved = resolveAggregatedValue(
                patient,
                badge.key as keyof MageXPatient,
                schema[badge.key]?.type,
              );
              const value = formatBadgeValue(
                resolved,
                schema[badge.key]?.value_labels,
              );
              const display = value || badge.empty_label || DEFAULT_BADGE_EMPTY;
              return (
                <span
                  key={badge.key}
                  className="bg-gradient-to-r from-primary to-primary/90 text-white px-4 py-1.5 rounded-lg font-medium shadow-sm text-sm"
                >
                  {display}
                </span>
              );
            })}
          </div>
        )}
        {(birthday || sexe || detailsSlot) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-600 mt-3">
            {birthday && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-slate-400" />
                {birthday}
              </span>
            )}
            {sexe && (
              <span className="flex items-center gap-1.5">
                <User className="h-4 w-4 text-slate-400" />
                {sexe}
              </span>
            )}
            {detailsSlot}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
